/**
 * useSquarePage Hook - 广场页面共享逻辑
 * 提取 Electron 和 Web 端 Square 页面的公共状态和业务逻辑
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import {
  subscribeCloudTagGroup,
  unsubscribeTagGroup,
  checkSubscriptionStatus,
  // Cloud 公开 API
  getCloudPublicTagGroups,
  getCloudPublicTags,
  getCloudPublicTopImages,
  getCloudPublicTagGroupDetail,
  getCloudPublicTagDetail,
  verifyCloudTagGroupPassword,
  verifyCloudTagPassword,
} from '../apiClient';
import type { CloudPublicTagGroup, CloudPublicTag } from '../apiClient';
import { sortCloudPosts } from '../utils/postConverter';
import { type SortBy } from '../utils/postList';
import { markAsViewed, hasUpdate as checkHasUpdate } from '../utils/viewedItems';
import type { CloudPost } from '../types';

export interface UseSquarePageOptions {
  /** 当前用户 ID */
  userId?: string;
  /** 是否已登录 */
  isLoggedIn: boolean;
  /** URL 中的 itemId 参数 */
  urlItemId?: string;
  /** 导航函数 */
  navigate: (path: string) => void;
  /** 页面模式：'square' 显示所有公开项，'subscriptions' 只显示已订阅项 */
  mode?: 'square' | 'subscriptions';
  /** 已订阅项的 ID 集合（仅 mode='subscriptions' 时需要） */
  subscribedItemIds?: Set<string>;
}

export interface UseSquarePageReturn {
  // 加载状态
  loading: boolean;
  loadingContent: boolean;
  loadingMore: boolean;
  /** 图片是否正在加载（用于显示骨架屏） */
  imagesLoading: boolean;

  // 数据
  posts: CloudPost[];
  filteredPosts: CloudPost[];

  // Cloud 数据
  cloudTagGroups: CloudPublicTagGroup[];
  cloudTags: CloudPublicTag[];
  cloudTopImages: Record<string, string[]>;
  cloudTopBloggers: Record<string, Array<{ nickname: string; avatarUrl: string | null }>>;

  // 选中状态
  selectedItemId: string | null;
  selectedItemType: 'tagGroup' | 'tag' | null;
  selectedItemDetail: any | null;
  selectedCloudTag: { groupId: string; tagName: string } | null;

  // 订阅状态
  subscriptionStatusMap: Record<string, boolean>;

  // 排序
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;

  // Tag Group 展开状态
  expandedTagGroups: Record<string, boolean>;
  tagGroupTags: Record<string, string[]>;

  // 分类数据
  myCloudTagGroups: CloudPublicTagGroup[];
  othersCloudTagGroups: CloudPublicTagGroup[];
  myCloudTags: CloudPublicTag[];
  othersCloudTags: CloudPublicTag[];

  // 操作方法
  clearSelection: () => void;
  handleSelectCloudTagGroup: (itemId: string) => void;
  handleSelectCloudTag: (itemId: string) => void;
  handleSelectCloudSubTag: (groupId: string, tagName: string) => void;
  handleToggleExpand: (itemId: string) => void;
  handleSubscribe: (itemId: string, itemType: 'tagGroup' | 'tag') => Promise<void>;
  handleUnsubscribe: (itemId: string, itemType: 'tagGroup' | 'tag') => Promise<void>;
  loadItemDetail: (itemId: string, itemType: 'tagGroup' | 'tag') => Promise<void>;

  // 无限滚动加载
  hasMore: boolean;
  loadMorePosts: () => Promise<void>;

  // 密码验证状态
  needsPassword: boolean;
  passwordError: string;
  verifyingPassword: boolean;
  handleVerifyPassword: (password: string) => Promise<void>;

  // 已验证的加密 item（itemId -> password 映射，用于自动重新验证）
  verifiedItems: Map<string, string>;

  // 更新红点检查函数
  checkItemHasUpdate: (itemId: string, updatedAt: Date | string | null) => boolean;
}

export function useSquarePage(options: UseSquarePageOptions): UseSquarePageReturn {
  const { userId, isLoggedIn, urlItemId, navigate, mode = 'square', subscribedItemIds } = options;

  // 加载状态
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(true);

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const POSTS_PER_PAGE = 1000;

  // 数据状态
  const [posts, setPosts] = useState<CloudPost[]>([]);

  // Cloud 数据状态
  const [cloudTagGroups, setCloudTagGroups] = useState<CloudPublicTagGroup[]>([]);
  const [cloudTags, setCloudTags] = useState<CloudPublicTag[]>([]);
  const [cloudTopImages, setCloudTopImages] = useState<Record<string, string[]>>({});
  const [cloudTopBloggers, setCloudTopBloggers] = useState<Record<string, Array<{ nickname: string; avatarUrl: string | null }>>>({});

  // 选中状态
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItemType, setSelectedItemType] = useState<'tagGroup' | 'tag' | null>(null);
  const [selectedItemDetail, setSelectedItemDetail] = useState<any | null>(null);
  const [selectedCloudTag, setSelectedCloudTag] = useState<{ groupId: string; tagName: string } | null>(null);

  // 订阅状态
  const [subscriptionStatusMap, setSubscriptionStatusMap] = useState<Record<string, boolean>>({});

  // 排序
  const [sortBy, setSortBy] = useState<SortBy>('blogger');

  // Tag Group 展开状态和子标签
  const [expandedTagGroups, setExpandedTagGroups] = useState<Record<string, boolean>>({});
  const [tagGroupTags, setTagGroupTags] = useState<Record<string, string[]>>({});

  // 密码验证状态
  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  // 已验证的 item 缓存（使用 localStorage 持久化，关闭浏览器后仍有效）
  // 存储 itemId -> password 映射，用于自动重新验证
  const [verifiedItems, setVerifiedItems] = useState<Map<string, string>>(() => {
    try {
      const cached = localStorage.getItem('square_verified_items_v2');
      if (cached) {
        return new Map(JSON.parse(cached));
      }
    } catch (e) {
      // ignore
    }
    return new Map();
  });

  // 保存已验证 item 和密码到 localStorage
  const addVerifiedItem = useCallback((itemId: string, password: string) => {
    setVerifiedItems(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, password);
      try {
        localStorage.setItem('square_verified_items_v2', JSON.stringify([...newMap]));
      } catch (e) {
        // ignore
      }
      return newMap;
    });
  }, []);

  // 使用 ref 追踪最新的 verifiedItems，避免 loadItemDetail 因其变化而重建
  const verifiedItemsRef = useRef(verifiedItems);
  verifiedItemsRef.current = verifiedItems;

  // 加载广场数据
  const loadSquareData = useCallback(async () => {
    setLoading(true);
    setImagesLoading(true);
    try {
      // 并行加载 Cloud 数据
      const [cloudTagGroupsResult, cloudTagsResult] = await Promise.all([
        getCloudPublicTagGroups({ page: 1, limit: 50, sortBy: 'latest' }),
        getCloudPublicTags({ page: 1, limit: 50, sortBy: 'latest' }),
      ]);

      const cloudTG = cloudTagGroupsResult?.tagGroups || [];
      const cloudT = cloudTagsResult?.tags || [];

      setCloudTagGroups(cloudTG);
      setCloudTags(cloudT);

      // 异步加载 topImages 和 topBloggers
      setTimeout(async () => {
        try {
          const allCloudIds = {
            tagGroupIds: cloudTG.map((tg: CloudPublicTagGroup) => tg.id),
            tagIds: cloudT.map((t: CloudPublicTag) => t.id),
          };
          if (allCloudIds.tagGroupIds.length > 0 || allCloudIds.tagIds.length > 0) {
            const cloudImagesResult = await getCloudPublicTopImages(allCloudIds);
            if (cloudImagesResult?.topImages) {
              setCloudTopImages(cloudImagesResult.topImages);
            }
            if (cloudImagesResult?.topBloggers) {
              setCloudTopBloggers(cloudImagesResult.topBloggers);
            }
          }
        } catch (e) {
          console.warn('Failed to load cloud topImages:', e);
        } finally {
          setImagesLoading(false);
        }
      }, 150);
    } catch (error) {
      console.error('Failed to load square data:', error);
      message.error('加载失败，请刷新重试');
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载选中项的详情
  const loadItemDetail = useCallback(async (itemId: string, itemType: 'tagGroup' | 'tag') => {
    setLoadingContent(true);
    setPasswordError(''); // 清除之前的错误
    setCurrentPage(1); // 重置分页
    setHasMore(false);
    try {
      let detail: any = null;
      let responsePosts: any[] = [];
      let responseTags: string[] = [];
      let responseNeedsPassword = false;
      let responsePagination: any = null;

      // Cloud 数据 - API 返回 { tagGroup/tag, posts, tags, needsPassword, pagination }
      if (itemType === 'tagGroup') {
        const result = await getCloudPublicTagGroupDetail(itemId, { page: 1, limit: POSTS_PER_PAGE });
        detail = result?.tagGroup || result;
        responsePosts = result?.posts || [];
        responseTags = result?.tags || [];
        responseNeedsPassword = result?.needsPassword || false;
        responsePagination = (result as any)?.pagination;
      } else {
        const result = await getCloudPublicTagDetail(itemId, { page: 1, limit: POSTS_PER_PAGE });
        detail = result?.tag || result;
        responsePosts = result?.posts || [];
        responseNeedsPassword = result?.needsPassword || false;
        responsePagination = (result as any)?.pagination;
      }

      // 如果 API 返回需要密码，检查是否有缓存的密码可以自动验证
      if (responseNeedsPassword && verifiedItemsRef.current.has(itemId)) {
        const cachedPassword = verifiedItemsRef.current.get(itemId)!;
        try {
          let verifyResult: any;
          if (itemType === 'tagGroup') {
            verifyResult = await verifyCloudTagGroupPassword(itemId, cachedPassword);
          } else {
            verifyResult = await verifyCloudTagPassword(itemId, cachedPassword);
          }

          if (verifyResult.valid) {
            // 自动验证成功，使用验证后的数据
            responseNeedsPassword = false;
            responsePosts = verifyResult.posts || [];
            responseTags = verifyResult.tags || [];
            detail = verifyResult.tagGroup || verifyResult.tag || detail;
          } else {
            // 密码已变更，清除缓存
            setVerifiedItems(prev => {
              const newMap = new Map(prev);
              newMap.delete(itemId);
              localStorage.setItem('square_verified_items_v2', JSON.stringify([...newMap]));
              return newMap;
            });
          }
        } catch (e) {
          console.warn('Auto-verify failed:', e);
          // 自动验证失败，让用户重新输入密码
        }
      }

      // 设置分页状态
      if (responsePagination) {
        setHasMore(responsePagination.hasMore || false);
      }

      // 设置需要密码状态
      setNeedsPassword(responseNeedsPassword);

      if (detail) {
        setSelectedItemDetail(detail);

        // 对于 Tag Group，提取 tags 列表
        if (itemType === 'tagGroup') {
          if (responseTags.length > 0) {
            setTagGroupTags(prev => ({ ...prev, [itemId]: responseTags }));
          } else if (responsePosts.length > 0) {
            // 从帖子中提取 tags
            const extractedTags = new Set<string>();
            responsePosts.forEach((post: any) => {
              if (post.tags) {
                post.tags.forEach((tag: any) => {
                  const tagName = typeof tag === 'string' ? tag : tag.name;
                  if (tagName) extractedTags.add(tagName);
                });
              }
            });
            const tagsList = Array.from(extractedTags).sort();
            setTagGroupTags(prev => ({ ...prev, [itemId]: tagsList }));
          }
        }

        // 帖子数据
        const postsData: CloudPost[] = responsePosts as CloudPost[];

        // 排序
        setPosts(sortCloudPosts(postsData));

        // 标记为已查看（用于更新红点）
        markAsViewed(itemId);

        // 检查订阅状态
        if (isLoggedIn) {
          try {
            const status = await checkSubscriptionStatus(itemId);
            setSubscriptionStatusMap(prev => ({ ...prev, [itemId]: status.isSubscribed }));
          } catch (e) {
            // 忽略
          }
        }
      }
    } catch (error) {
      console.error('Failed to load item detail:', error);
      message.error('加载详情失败');
    } finally {
      setLoadingContent(false);
    }
  }, [isLoggedIn]);

  // 加载更多帖子（无限滚动）
  const loadMorePosts = useCallback(async () => {
    if (!selectedItemId || !selectedItemType || loadingMore || !hasMore) {
      return;
    }

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      let responsePosts: any[] = [];
      let responsePagination: any = null;

      if (selectedItemType === 'tagGroup') {
        const result = await getCloudPublicTagGroupDetail(selectedItemId, { page: nextPage, limit: POSTS_PER_PAGE });
        responsePosts = result?.posts || [];
        responsePagination = (result as any)?.pagination;
      } else {
        const result = await getCloudPublicTagDetail(selectedItemId, { page: nextPage, limit: POSTS_PER_PAGE });
        responsePosts = result?.posts || [];
        responsePagination = (result as any)?.pagination;
      }

      if (responsePosts.length > 0) {
        const newPosts = responsePosts as CloudPost[];
        setPosts(prev => [...prev, ...newPosts]);
        setCurrentPage(nextPage);
      }

      if (responsePagination) {
        setHasMore(responsePagination.hasMore || false);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more posts:', error);
      message.error('加载更多失败');
    } finally {
      setLoadingMore(false);
    }
  }, [selectedItemId, selectedItemType, currentPage, loadingMore, hasMore]);

  // 处理订阅
  const handleSubscribe = useCallback(async (itemId: string, _itemType: 'tagGroup' | 'tag') => {
    if (!isLoggedIn) {
      message.warning('请先登录');
      navigate('/auth/login?redirect=/square');
      return;
    }

    try {
      await subscribeCloudTagGroup(itemId);
      setSubscriptionStatusMap(prev => ({ ...prev, [itemId]: true }));
      message.success('订阅成功');
    } catch (error: any) {
      if (error?.code === 'subscription_limit_reached') {
        message.error('免费用户最多订阅 50 个，升级 Pro 享受无限订阅');
      } else if (error?.code === 'already_subscribed') {
        message.info('您已订阅该项');
        setSubscriptionStatusMap(prev => ({ ...prev, [itemId]: true }));
      } else {
        message.error(error?.message || '订阅失败');
      }
    }
  }, [isLoggedIn, navigate]);

  // 取消订阅
  const handleUnsubscribe = useCallback(async (itemId: string, _itemType: 'tagGroup' | 'tag') => {
    try {
      await unsubscribeTagGroup(itemId);
      setSubscriptionStatusMap(prev => ({ ...prev, [itemId]: false }));
      message.success('已取消订阅');
    } catch (error: any) {
      message.error(error?.message || '操作失败');
    }
  }, []);

  // 初始加载
  useEffect(() => {
    loadSquareData();
  }, [loadSquareData]);

  // 从 URL 参数初始化选中项
  useEffect(() => {
    if (loading) return;

    // 没有 urlItemId 时，清除选中状态（回到初始态）
    if (!urlItemId) {
      setSelectedItemId(null);
      setSelectedItemType(null);
      setSelectedCloudTag(null);
      setSelectedItemDetail(null);
      setPosts([]);
      return;
    }

    // Cloud 路由: cloud-taggroup-xxx, cloud-tag-xxx
    if (urlItemId.startsWith('cloud-taggroup-')) {
      const rest = urlItemId.replace('cloud-taggroup-', '');
      const tagSeparatorIndex = rest.indexOf('-tag-');

      if (tagSeparatorIndex !== -1) {
        const groupId = rest.substring(0, tagSeparatorIndex);
        const tagName = decodeURIComponent(rest.substring(tagSeparatorIndex + 5));
        setSelectedItemId(groupId);
        setSelectedItemType('tagGroup');
        setSelectedCloudTag({ groupId, tagName });
        setExpandedTagGroups(prev => ({ ...prev, [groupId]: true }));
      } else {
        setSelectedItemId(rest);
        setSelectedItemType('tagGroup');
        setSelectedCloudTag(null);
      }
    } else if (urlItemId.startsWith('cloud-tag-')) {
      const tagId = urlItemId.replace('cloud-tag-', '');
      setSelectedItemId(tagId);
      setSelectedItemType('tag');
      setSelectedCloudTag(null);
    }
  }, [urlItemId, loading]);

  // 选中项变化时加载详情
  useEffect(() => {
    if (selectedItemId && selectedItemType) {
      loadItemDetail(selectedItemId, selectedItemType);
    }
  }, [selectedItemId, selectedItemType, loadItemDetail]);

  // 清除选中状态（回到初始态）
  const clearSelection = useCallback(() => {
    navigate('/square');
  }, [navigate]);

  // 子标签选择
  const handleSelectCloudSubTag = useCallback((groupId: string, tagName: string) => {
    navigate(`/square/cloud-taggroup-${groupId}-tag-${encodeURIComponent(tagName)}`);
  }, [navigate]);

  // Cloud 导航处理
  const handleSelectCloudTagGroup = useCallback((itemId: string) => {
    navigate(`/square/cloud-taggroup-${itemId}`);
  }, [navigate]);

  const handleSelectCloudTag = useCallback((itemId: string) => {
    navigate(`/square/cloud-tag-${itemId}`);
  }, [navigate]);

  const handleToggleExpand = useCallback((itemId: string) => {
    setExpandedTagGroups(prev => ({ ...prev, [itemId]: !prev[itemId] }));
    if (!expandedTagGroups[itemId] && !tagGroupTags[itemId]) {
      loadItemDetail(itemId, 'tagGroup');
    }
  }, [expandedTagGroups, tagGroupTags, loadItemDetail]);

  // 根据选中的子 tag 过滤帖子
  const filteredPosts = selectedCloudTag
    ? posts.filter(post => post.tags?.some((tag: { name: string }) => tag.name === selectedCloudTag.tagName))
    : posts;

  // 订阅过滤函数
  const filterBySubscription = <T extends { id: string }>(items: T[]): T[] => {
    if (mode === 'subscriptions' && subscribedItemIds) {
      return items.filter(item => subscribedItemIds.has(item.id));
    }
    return items;
  };

  // Cloud 数据分类
  const allCloudTagGroups = filterBySubscription(
    cloudTagGroups.filter(tg => tg && tg.id)
      .sort((a, b) => new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime())
  );
  const myCloudTagGroups = mode === 'subscriptions' ? [] : allCloudTagGroups.filter(item => item.uploader?.id === userId);
  const othersCloudTagGroups = mode === 'subscriptions' ? allCloudTagGroups : allCloudTagGroups.filter(item => item.uploader?.id !== userId);

  const allCloudTags = filterBySubscription(
    cloudTags.filter(t => t && t.id)
      .sort((a, b) => new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime())
  );
  const myCloudTags = mode === 'subscriptions' ? [] : allCloudTags.filter(item => item.uploader?.id === userId);
  const othersCloudTags = mode === 'subscriptions' ? allCloudTags : allCloudTags.filter(item => item.uploader?.id !== userId);

  // 密码验证处理
  const handleVerifyPassword = useCallback(async (password: string) => {
    if (!selectedItemId || !selectedItemType) return;

    setVerifyingPassword(true);
    setPasswordError('');

    try {
      let result: any;
      if (selectedItemType === 'tagGroup') {
        result = await verifyCloudTagGroupPassword(selectedItemId, password);
      } else {
        result = await verifyCloudTagPassword(selectedItemId, password);
      }

      if (result.valid) {
        // 验证成功，缓存已验证的 item 和密码（使用 localStorage 持久化）
        addVerifiedItem(selectedItemId, password);
        setNeedsPassword(false);

        // 更新帖子数据
        const responsePosts = result.posts || [];
        const responseTags = result.tags || [];

        const postsData = responsePosts as CloudPost[];
        setPosts(sortCloudPosts(postsData));

        // 更新 tags 列表
        if (selectedItemType === 'tagGroup' && responseTags.length > 0) {
          setTagGroupTags(prev => ({ ...prev, [selectedItemId]: responseTags }));
        }
      } else {
        setPasswordError(result.message || '密码错误');
      }
    } catch (error: any) {
      setPasswordError(error?.message || '验证失败');
    } finally {
      setVerifyingPassword(false);
    }
  }, [selectedItemId, selectedItemType, addVerifiedItem]);

  return {
    // 加载状态
    loading,
    loadingContent,
    loadingMore,
    imagesLoading,

    // 数据
    posts,
    filteredPosts,

    // Cloud 数据
    cloudTagGroups,
    cloudTags,
    cloudTopImages,
    cloudTopBloggers,

    // 选中状态
    selectedItemId,
    selectedItemType,
    selectedItemDetail,
    selectedCloudTag,

    // 订阅状态
    subscriptionStatusMap,

    // 排序
    sortBy,
    setSortBy,

    // Tag Group 展开状态
    expandedTagGroups,
    tagGroupTags,

    // 分类数据
    myCloudTagGroups,
    othersCloudTagGroups,
    myCloudTags,
    othersCloudTags,

    // 操作方法
    clearSelection,
    handleSelectCloudTagGroup,
    handleSelectCloudTag,
    handleSelectCloudSubTag,
    handleToggleExpand,
    handleSubscribe,
    handleUnsubscribe,
    loadItemDetail,

    // 无限滚动加载
    hasMore,
    loadMorePosts,

    // 密码验证状态
    needsPassword,
    passwordError,
    verifyingPassword,
    handleVerifyPassword,

    // 已验证的加密 item（用于恢复展开功能）
    verifiedItems,

    // 更新红点检查函数
    checkItemHasUpdate: checkHasUpdate,
  };
}
